import { useFormContext } from 'react-hook-form';
import type { CampaignEntry } from '../api';
import { useCampaigns } from '../api';
import { useCallback, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import type {
  GridColDef,
  GridColumnHeaderParams,
  GridRenderCellParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import { DataGrid } from '@mui/x-data-grid';
import { Checkbox, ChipInput, CircularProgress, SuspenseErrorFallback } from 'components/common';
import { FormContainer } from 'components/layout';
import { CampaignStatusSwitcher, DuplicateStatusMenu } from './FacebookFormComponents';
import { CAMPAIGN_NAME_FILTER } from '../constants';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { isEmpty } from 'lodash-es';
import { isErrorWithCause } from 'utils';
import type { MarketingProcess } from 'types/shared';

const CAMPAIGNS_LIMIT = 20;

const createColumns = (isMarketingProcessInbox: boolean): GridColDef<CampaignEntry>[] => [
  {
    field: 'id',
    headerName: 'Campaign ID',
    width: 160,
  },
  {
    field: 'name',
    headerName: 'Campaign Name',
    width: 750,
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 180,
    sortable: false,
    type: 'singleSelect',
    valueOptions: ['ACTIVE', 'PAUSED'],
    renderHeader: (params: GridColumnHeaderParams<CampaignEntry>) => {
      if (!isMarketingProcessInbox) {
        return <DuplicateStatusMenu formField="selectedCampaigns" />;
      }
      return params.colDef.headerName;
    },
    renderCell: (params: GridRenderCellParams<CampaignEntry, CampaignEntry['status']>) => {
      if (!isMarketingProcessInbox) {
        return <CampaignStatusSwitcher cellParams={params} />;
      }
      return params.row.status;
    },
  },
];

const useCampaignSelection = () => {
  const { watch, setValue } = useFormContext();

  const selectedCampaignsRows = watch('selectedCampaigns') as CampaignEntry[];

  const selectedCampaigns = useMemo(
    () => selectedCampaignsRows?.map((c) => c.id) ?? [],
    [selectedCampaignsRows],
  );

  const updateSelectedCampaigns = useCallback(
    (campaigns: CampaignEntry[]) => {
      setValue('selectedCampaigns', campaigns, { shouldValidate: true });
    },
    [setValue],
  );

  return {
    selectedCampaigns,
    selectedCampaignsRows,
    updateSelectedCampaigns,
  };
};

const useWatchedFormValues = () => {
  const { watch } = useFormContext();
  const adAccounts = watch('adAccounts');
  const marketingProcess = watch('marketingProcess');
  const filterCampaignsByName = watch('filterCampaignsByName');
  const selectedCampaignsOnly: boolean = watch('selectedCampaignsOnly');

  return {
    facebookAccountId: adAccounts,
    marketingProcess: marketingProcess as MarketingProcess,
    filterByName: filterCampaignsByName,
    selectedCampaignsOnly,
  };
};

const LoadingOverlay = ({ title }: { title: string }) => (
  <Box
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      display: 'flex',
      alignContent: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
      zIndex: 2,
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
    }}
  >
    <CircularProgress title={title} />
  </Box>
);

const DataGridFooter = ({ selectedCount }: { selectedCount: number }) => {
  if (!selectedCount) return null;

  return (
    <Box
      sx={(theme) => ({
        p: 2,
        height: 54,
        minHeight: 54,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderTop: `1px solid ${theme.palette.divider}`,
        cursor: 'default',
      })}
    >
      {selectedCount === CAMPAIGNS_LIMIT ? (
        <>
          <InfoIcon color="primary" sx={{ fontSize: 21 }} />
          <Typography variant="body2">
            {`Up to ${CAMPAIGNS_LIMIT} campaigns can be selected`}
          </Typography>
        </>
      ) : (
        <Typography variant="body2">
          {selectedCount} rows selected
        </Typography>
      )}
    </Box>
  );
};

const EmptyState = ({ message }: { message: string }) => (
  <Typography
    sx={(theme) => ({
      color: theme.palette.grey[400],
      fontSize: theme.typography.h4.fontSize,
    })}
  >
    {message}
  </Typography>
);

const CampaignsGrid = () => {
  const { facebookAccountId, marketingProcess, filterByName, selectedCampaignsOnly } = useWatchedFormValues();
  const { selectedCampaigns, selectedCampaignsRows, updateSelectedCampaigns } = useCampaignSelection();

  const isMarketingProcessInbox = useMemo(
    () => CAMPAIGN_NAME_FILTER[marketingProcess]?.includes('inbox'),
    [marketingProcess],
  );

  const columns = useMemo(
    () => createColumns(isMarketingProcessInbox),
    [isMarketingProcessInbox],
  );

  const {
    data: rows,
    isPending,
    isError,
    error,
  } = useCampaigns({
    params: {
      facebookAccountId,
      campaignNameFilter: CAMPAIGN_NAME_FILTER[marketingProcess] ?? '',
      query: filterByName,
    },
  });

  const filteredRows = useMemo(() => {
    if (!rows?.length && !selectedCampaignsRows?.length) return [];

    const rowsSet = new Set(rows?.map(r => r.id) ?? []);
    const previousSelected =
      selectedCampaignsRows?.filter(
        (campaign) => !rowsSet.has(campaign.id),
      ) ?? [];

    return [...previousSelected, ...(rows ?? [])]
      .filter(row => !selectedCampaignsOnly || selectedCampaigns.includes(row.id));
  }, [rows, selectedCampaignsRows, selectedCampaignsOnly, selectedCampaigns]);

  const handleSelectionMode = useCallback(
    (selectedRows: GridRowSelectionModel) => {
      if (!rows) return;

      const parsedSelectedRows = selectedRows.slice(0, CAMPAIGNS_LIMIT);

      const rowsMap = new Map(rows.map(row => [String(row.id), row]));
      const selectedRowsMap = new Map(
        selectedCampaignsRows?.map(row => [String(row.id), row]) ?? [],
      );

      const currentSelected = parsedSelectedRows
        .map(id => {
          const row = rowsMap.get(String(id));
          if (!row) return null;

          return {
            ...row,
            status: selectedRowsMap.get(String(id))?.status ?? row.status,
          };
        })
        .filter((campaign): campaign is CampaignEntry => campaign !== null);

      const previousSelected = selectedCampaignsRows?.filter(
        (campaign) =>
          !rowsMap.has(campaign.id) && parsedSelectedRows.includes(campaign.id),
      ) ?? [];

      const allSelectedCampaigns = [...previousSelected, ...currentSelected];
      updateSelectedCampaigns(allSelectedCampaigns);
    },
    [rows, selectedCampaignsRows, updateSelectedCampaigns],
  );

  if (!marketingProcess) {
    return <EmptyState message="Marketing process is required" />;
  }

  if (isError) {
    const errorMessage = isErrorWithCause(error)
      ? error?.cause?.error ?? error?.message ?? 'An error occurred while fetching campaigns'
      : 'An error occurred while fetching campaigns';

    return (
      <SuspenseErrorFallback
        title="Choose Campaigns"
        message={errorMessage}
      />
    );
  }

  if (isPending && isEmpty(filteredRows)) {
    return <CircularProgress title="Campaigns are loading..." />;
  }

  if (isEmpty(filteredRows)) {
    return <EmptyState message="No campaigns found" />;
  }

  return (
    <Box style={{ position: 'relative', height: 380, width: '100%' }}>
      {isPending && <LoadingOverlay title="Campaigns are loading..." />}

      <DataGrid
        rows={filteredRows}
        columns={columns}
        checkboxSelection
        rowSelectionModel={selectedCampaigns}
        onRowSelectionModelChange={handleSelectionMode}
        hideFooterPagination
        disableColumnMenu
        getRowId={({ id }) => id}
        isRowSelectable={(params) =>
          selectedCampaigns.length < CAMPAIGNS_LIMIT ||
          selectedCampaigns.includes(params.row.id)
        }
        slots={{
          footer: () => <DataGridFooter selectedCount={selectedCampaigns.length} />,
        }}
        sx={{
          '& .MuiDataGrid-main': {
            overflow: 'hidden',
          },
        }}
      />
    </Box>
  );
};

const CampaignsForm = () => {
  return (
    <FormContainer
      title="Choose campaigns"
      sx={{ flexDirection: 'column', gap: 2 }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flex: 2,
            flexDirection: 'row',
            alignItems: 'center',
            flexWrap: 'nowrap',
          }}
        >
          <ChipInput
            name="filterCampaignsByName"
            inputLabel="Enter campaign name"
          />
          <Checkbox
            dataTestId={'selectedCampaignsOnly'}
            name={'selectedCampaignsOnly'}
            label={'Selected only'}
            controlLabelSx={{ whiteSpace: 'nowrap' }}
          />
        </Box>
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}></Box>
      </Box>
      <CampaignsGrid />
    </FormContainer>
  );
};

export default CampaignsForm;